import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { NovaMark } from '../design/Nova';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEngine } from '../api/context';
import { useMetaIndex } from '../meta/useMeta';
import { Icon } from '../design/icons';
import { Breadcrumb, Kbd, type Crumb } from '../design/primitives/Misc';
import { IconButton } from '../design/primitives/Button';
import { Tooltip } from '../design/primitives/Overlay';
import { useTheme } from '../design/theme';
import { InspectorDock, useInspectorRouteReset } from '../views/Inspector';
import { FeedbackButton } from '../views/FeedbackButton';
import { CommandPalette } from './CommandPalette';
import { NotificationTray } from './NotificationTray';
import { ShortcutSheet, useHotkeys } from './shortcuts';
import { UserMenu } from '../shell/UserMenu';
import { StaleBuildBanner } from '../shell/StaleBuildBanner';
import type { NavSection } from '../module';
import { VersionSkewChip } from '../shell/VersionSkewChip';

// AppShell: sidebar generated from every module's nav (collapsible to an icon rail, off-
// canvas drawer on small screens), topbar (⌘K search, notifications, theme, user menu),
// breadcrumbs from the route, keyed route transitions, and the Inspector dock.

export interface AppShellProps {
  brand?: ReactNode;
  brandShort?: ReactNode;
  /** Extra content at the bottom of the sidebar. */
  footer?: ReactNode;
  /** Badge counts by nav `badge` key. */
  badges?: Record<string, number | string>;
  /** Pre-merged nav sections (from the Shell). When omitted, derived from the modules. */
  sections?: NavSection[];
}

const COLLAPSE_KEY = 'hf.sidebar.collapsed';

export function AppShell({ brand = 'NovaPSA', brandShort = 'Nova', footer, badges, sections: sectionsProp }: AppShellProps) {
  const { modules } = useEngine();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  useInspectorRouteReset(location.pathname);
  useEffect(() => setMobileOpen(false), [location.pathname]);
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  const sections = useMemo<NavSection[]>(() => {
    if (sectionsProp) return sectionsProp;
    const all = modules.flatMap((m) => m.nav);
    const merged = new Map<string, NavSection>();
    for (const s of all) {
      const e = merged.get(`${s.area ?? ''}::${s.label}`);
      if (e) e.items.push(...s.items);
      // Config sections share their area heading with a work section ("Service desk" at work
      // and in Configuration): keep them apart and say which is which in the flat sidebar.
      else merged.set(`${s.area ?? ''}::${s.label}`, { ...s, label: s.area === 'config' ? `Configuration · ${s.label}` : s.label, items: [...s.items] });
    }
    return [...merged.values()].sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
  }, [modules, sectionsProp]);

  const goHotkeys = sections.flatMap((s) => s.items.filter((i) => i.key).map((i) => ({ keys: `g ${i.key}`, run: () => navigate(i.to), label: `Go to ${i.label}`, group: 'Navigate' })));
  useHotkeys([
    { keys: 'mod+k', run: () => setPaletteOpen((v) => !v), label: 'Command palette', group: 'General', global: true },
    { keys: '?', run: () => setSheetOpen(true), label: 'Keyboard shortcuts', group: 'General' },
    { keys: '[', run: () => setCollapsed((v) => !v), label: 'Toggle sidebar', group: 'General' },
    { keys: 'mod+shift+l', run: () => theme.toggle(), label: 'Toggle theme', group: 'General', global: true },
    { keys: 'j', run: () => undefined, label: 'Next row', group: 'Lists' },
    { keys: 'k', run: () => undefined, label: 'Previous row', group: 'Lists' },
    { keys: '/', run: () => undefined, label: 'Search the list', group: 'Lists' },
    { keys: 'n', run: () => undefined, label: 'New record', group: 'Lists' },
    { keys: 'e', run: () => undefined, label: 'Edit (record / cell)', group: 'Lists' },
    { keys: 'x', run: () => undefined, label: 'Select row', group: 'Lists' },
    ...goHotkeys,
  ]);

  const crumbs = useBreadcrumbs();

  return (
    <div className={['hf-shell', collapsed && 'collapsed', mobileOpen && 'nav-open'].filter(Boolean).join(' ')}>
      <a href="#hf-main" className="hf-skip">
        Skip to content
      </a>
      {mobileOpen && <div className="hf-shell-scrim" onClick={() => setMobileOpen(false)} aria-hidden />}
      <aside className="hf-sidebar" aria-label="Sidebar">
        <div className="hf-brand">
          <Link to="/" className="hf-brand-home" aria-label={typeof brand === 'string' ? `${brand} — home` : 'Home'}>
            <NovaMark size={22} className="mark" />
            <span className="name hf-display">{collapsed ? brandShort : brand}</span>
          </Link>
          <IconButton icon={collapsed ? 'chevrons-right' : 'chevrons-left'} label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} size="sm" className="collapse" onClick={() => setCollapsed((v) => !v)} />
        </div>
        <button type="button" className="hf-search-trigger" onClick={() => setPaletteOpen(true)} aria-label="Search and commands">
          <Icon name="search" size={14} />
          <span className="lbl">Search…</span>
          <span className="kbd">
            <Kbd>⌘K</Kbd>
          </span>
        </button>
        <nav className="hf-nav hf-scroll" aria-label="Primary">
          {sections.map((s) => (
            <div key={`${s.area ?? ''}::${s.label}`} className="hf-nav-section">
              {/* The flat sidebar has no area rail, so say which "Service desk" this is. */}
              <div className="hf-nav-label">{s.area === 'config' && !s.label.startsWith('Configuration') ? `Configuration · ${s.label}` : s.label}</div>
              {s.items.map((it) => {
                const badge = typeof it.badge === 'string' ? badges?.[it.badge] : it.badge;
                const link = (
                  <NavLink key={it.to} to={it.to} end={it.end} className={({ isActive }) => ['hf-nav-item', isActive && 'active'].filter(Boolean).join(' ')}>
                    <Icon name={it.icon ?? 'circle-dot'} size={16} />
                    <span className="lbl">{it.label}</span>
                    {badge != null && badge !== 0 && <span className="hf-nav-badge hf-num">{badge}</span>}
                  </NavLink>
                );
                return collapsed ? (
                  <Tooltip key={it.to} content={it.label} kbd={it.key ? `g ${it.key}` : undefined} placement="right-start">
                    {link}
                  </Tooltip>
                ) : (
                  link
                );
              })}
            </div>
          ))}
        </nav>
        <div className="hf-sidebar-foot">
          {footer}
          <UserMenu placement="top-start" onShortcuts={() => setSheetOpen(true)} />
        </div>
      </aside>

      <div className="hf-main-col">
        <header className="hf-topbar">
          <IconButton icon="menu" label="Open navigation" size="sm" className="hf-hamburger" onClick={() => setMobileOpen(true)} />
          <div className="hf-topbar-crumbs">
            <Breadcrumb items={crumbs} />
          </div>
          <div className="hf-topbar-tools">
            <Tooltip content="Search" kbd="⌘K">
              <IconButton icon="search" label="Search" size="sm" onClick={() => setPaletteOpen(true)} />
            </Tooltip>
            <VersionSkewChip />
            <NotificationTray />
            <FeedbackButton />
            <Tooltip content={theme.resolved === 'dark' ? 'Light mode' : 'Dark mode'} kbd="⌘⇧L">
              <IconButton icon={theme.resolved === 'dark' ? 'sun' : 'moon'} label={theme.resolved === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} size="sm" onClick={theme.toggle} />
            </Tooltip>
          </div>
        </header>
        <div className="hf-body">
          <main id="hf-main" className="hf-content hf-scroll" tabIndex={-1}>
            <StaleBuildBanner />
            <div className="hf-route" key={location.pathname}>
              <Outlet />
            </div>
          </main>
          <InspectorDock />
        </div>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <ShortcutSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </div>
  );
}

/** Breadcrumbs from the current route: matching module screens (list → detail) or nav labels. */
export function useBreadcrumbs(): Crumb[] {
  const { modules } = useEngine();
  const location = useLocation();
  const metaIndex = useMetaIndex();
  return useMemo(() => {
    const path = location.pathname;
    const crumbs: Crumb[] = [{ label: 'Home', to: '/' }];
    if (path === '/') return crumbs;
    const screens = modules.flatMap((m) => m.screens);
    const navItems = modules.flatMap((m) => m.nav.flatMap((s) => s.items));
    // Find the deepest screen whose pattern matches.
    const match = screens
      .map((s) => ({ s, re: new RegExp('^' + s.path.replace(/:[^/]+/g, '([^/]+)') + '$') }))
      .find((x) => x.re.test(path));
    if (match) {
      const s = match.s;
      const meta = metaIndex.data?.resources.find((r) => r.name === s.resource);
      if (s.kind === 'detail') {
        const parent = s.path.replace(/\/:id$/, '');
        const parentScreen = screens.find((x) => x.path === parent);
        crumbs.push({ label: parentScreen?.title ?? meta?.labelPlural ?? parent.slice(1), to: parent });
        crumbs.push({ label: s.title ?? meta?.label ?? 'Record' });
      } else {
        crumbs.push({ label: s.title ?? meta?.labelPlural ?? navItems.find((n) => n.to === s.path)?.label ?? path.slice(1) });
      }
      return crumbs;
    }
    const nav = navItems.find((n) => n.to === path);
    crumbs.push({ label: nav?.label ?? path.slice(1).replace(/[-/]/g, ' ') });
    return crumbs;
  }, [location.pathname, modules, metaIndex.data]);
}
